import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Loader2, AlertTriangle, ChevronLeft, ChevronRight, Info } from "lucide-react";

/* In-browser DICOM viewer — patients/doctors preview MRI/CT slices
   without desktop software. Server pre-renders each slice to PNG using
   the file's own windowing, so the browser only needs to paint an image.

   Props:
     fileId       — /api/files/{id}/dicom/preview target
     authToken    — Bearer JWT (we pass it via ?auth= so <img> can fetch)
     originalName — displayed header
*/
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DicomViewer({ fileId, authToken, originalName }) {
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [frame, setFrame] = useState(0);
  const [totalFrames, setTotalFrames] = useState(1);
  const [imgLoading, setImgLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    let alive = true;
    setError(null);
    (async () => {
      try {
        const r = await fetch(`${API}/files/${fileId}/dicom/meta`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!r.ok) throw new Error((await r.json()).detail || "Could not read DICOM metadata");
        const m = await r.json();
        if (!alive) return;
        setMeta(m);
        const f = Math.max(1, m.frames || 1);
        setTotalFrames(f);
        setFrame(Math.floor(f / 2));
      } catch (err) {
        if (alive) setError(err?.message || "DICOM metadata error");
      }
    })();
    return () => { alive = false; };
  }, [fileId, authToken]);

  const src = useMemo(() => {
    if (!meta) return "";
    return `${API}/files/${fileId}/dicom/preview?frame=${frame}&auth=${encodeURIComponent(authToken)}`;
  }, [fileId, frame, authToken, meta]);

  const prev = useCallback(() => setFrame((f) => Math.max(0, f - 1)), []);
  const next = useCallback(() => setFrame((f) => Math.min(totalFrames - 1, f + 1)), [totalFrames]);

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50/60 p-4 text-sm text-red-800 flex items-start gap-2" data-testid="dicom-error">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          <div className="font-semibold">Can't preview this DICOM</div>
          <div className="text-red-700 mt-0.5">{error}</div>
        </div>
      </div>
    );
  }

  if (!meta) {
    return (
      <div className="flex items-center justify-center py-10 text-slate-500 text-sm" data-testid="dicom-loading">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Reading DICOM…
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-900 text-slate-100 overflow-hidden" data-testid="dicom-viewer">
      <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-slate-800/80 text-xs">
        <div className="flex items-center gap-2 min-w-0">
          <span className="px-1.5 py-0.5 rounded bg-blue-600/80 text-white font-semibold text-[10px] tracking-wider">{meta.modality || "DICOM"}</span>
          <span className="truncate text-slate-300" title={originalName}>{originalName}</span>
        </div>
        <button
          type="button"
          onClick={() => setShowDetails((s) => !s)}
          className="flex items-center gap-1 text-slate-300 hover:text-white"
          data-testid="dicom-toggle-details"
        >
          <Info className="w-3.5 h-3.5" /> {showDetails ? "Hide" : "Info"}
        </button>
      </div>

      {showDetails && (
        <div className="px-4 py-3 bg-slate-800/60 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-[11px] text-slate-300" data-testid="dicom-meta">
          {meta.study_description && <Field label="Study" value={meta.study_description} />}
          {meta.series_description && <Field label="Series" value={meta.series_description} />}
          {meta.body_part && <Field label="Body part" value={meta.body_part} />}
          {meta.study_date && <Field label="Date" value={formatDicomDate(meta.study_date)} />}
          {meta.manufacturer && <Field label="Scanner" value={`${meta.manufacturer} ${meta.model || ""}`.trim()} />}
          {meta.rows && meta.columns && <Field label="Dimensions" value={`${meta.columns} × ${meta.rows}`} />}
        </div>
      )}

      <div className="relative bg-black" style={{ minHeight: 320 }}>
        <img
          src={src}
          alt={`DICOM frame ${frame + 1}`}
          className="w-full h-auto max-h-[70vh] object-contain select-none"
          style={{ imageRendering: "pixelated" }}
          onLoad={() => setImgLoading(false)}
          onError={() => { setImgLoading(false); setError("Could not render slice"); }}
          draggable={false}
          data-testid="dicom-image"
        />
        {imgLoading && (
          <div className="absolute inset-0 flex items-center justify-center text-slate-400 pointer-events-none">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        )}
      </div>

      {totalFrames > 1 && (
        <div className="flex items-center gap-3 px-4 py-2.5 bg-slate-800/80">
          <button
            type="button"
            onClick={prev}
            disabled={frame === 0}
            className="p-1.5 rounded-md hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="dicom-prev-slice"
            aria-label="Previous slice"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <input
            type="range"
            min={0}
            max={totalFrames - 1}
            value={frame}
            onChange={(e) => { setImgLoading(true); setFrame(parseInt(e.target.value, 10)); }}
            className="flex-1 accent-blue-500"
            data-testid="dicom-slice-slider"
            aria-label="Slice"
          />
          <button
            type="button"
            onClick={next}
            disabled={frame === totalFrames - 1}
            className="p-1.5 rounded-md hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="dicom-next-slice"
            aria-label="Next slice"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <span className="text-xs font-mono text-slate-400 tabular-nums min-w-[60px] text-right" data-testid="dicom-slice-counter">
            {frame + 1} / {totalFrames}
          </span>
        </div>
      )}

      <div className="px-4 py-2 text-[10px] text-slate-500 italic bg-slate-900">
        Preview rendered server-side with the DICOM's own windowing. Download the original .dcm for full diagnostic quality.
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-slate-200 truncate" title={value}>{value}</div>
    </div>
  );
}

function formatDicomDate(yyyymmdd) {
  if (!yyyymmdd || yyyymmdd.length < 8) return yyyymmdd;
  const y = yyyymmdd.slice(0, 4);
  const m = yyyymmdd.slice(4, 6);
  const d = yyyymmdd.slice(6, 8);
  return `${d}/${m}/${y}`;
}
